import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { authApi } from '@/api';
import { Alert, Button, Input, useToast } from '@/components/ui';
import { CODE_LENGTH, CodeBoxes } from '@/features/auth/CodeBoxes';
import { applyFieldErrors, errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { forgotPasswordSchema, newPasswordSchema } from '@/validations';

import { AuthCard } from './AuthCard';

const BACK_TO_SIGN_IN = (
  <Link to={paths.login} className="text-brand-600 font-medium">
    Back to sign in
  </Link>
);

/**
 * The code step, shared by both pages: six boxes, checked as soon as the sixth
 * digit is in. The code is not used up here; the password step sends it again
 * with the new password.
 */
function ResetCodeStep({ email, onAccepted, onResend, isResending, debugCode }) {
  const [code, setCode] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  // Remounting the boxes after a wrong code puts the cursor back in the first.
  const [attempt, setAttempt] = useState(0);

  async function check(complete) {
    setChecking(true);
    setError(null);
    try {
      await authApi.checkResetCode(email, complete);
      onAccepted(complete);
    } catch (checkError) {
      setError(errorMessage(checkError, 'That code did not work'));
      setCode('');
      setAttempt((count) => count + 1);
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="space-y-4">
      <Alert tone="info">
        A 6-digit OTP has been sent to your email. It is valid for 10 minutes.
      </Alert>
      {debugCode && (
        <Alert tone="info" title="Development mode">
          Code: <span className="font-mono font-semibold">{debugCode}</span>
        </Alert>
      )}
      <div className="space-y-1.5">
        <CodeBoxes
          key={attempt}
          value={code}
          onChange={(next) => {
            setCode(next);
            if (error) setError(null);
          }}
          onComplete={(complete) => void check(complete)}
          disabled={checking}
          invalid={Boolean(error)}
          autoFocus
          label="Reset code"
        />
        {error && (
          <p className="text-caption text-danger-600" role="alert">
            {error}
          </p>
        )}
      </div>
      <Button
        fullWidth
        isLoading={checking}
        disabled={code.length !== CODE_LENGTH}
        onClick={() => void check(code)}
      >
        Continue
      </Button>
      {onResend && (
        <p className="text-ink-muted text-center text-sm">
          Didn&apos;t get it?{' '}
          <button
            type="button"
            className="text-brand-600 font-medium disabled:opacity-60"
            onClick={onResend}
            disabled={isResending}
          >
            Send a new code
          </button>
        </p>
      )}
    </div>
  );
}

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [sentTo, setSentTo] = useState(null);
  const [debugCode, setDebugCode] = useState(null);
  const [resending, setResending] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(forgotPasswordSchema) });

  async function send(email) {
    const result = await authApi.forgotPassword(email);
    setSentTo(email);
    setDebugCode(result.debug_code ?? null);
  }

  async function onSubmit(values) {
    try {
      await send(values.email);
    } catch (error) {
      // An unknown email comes back as a field error on `email`.
      if (!applyFieldErrors(error, setError)) {
        setError('root', { message: errorMessage(error, 'Could not send the code') });
      }
    }
  }

  async function resend() {
    setResending(true);
    try {
      await send(sentTo);
      toast.success('A new code is on its way.');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not send the code'));
    } finally {
      setResending(false);
    }
  }

  return (
    <AuthCard
      title={sentTo ? 'Enter your code' : 'Reset your password'}
      description={
        sentTo ? `Check ${sentTo} for a 6-digit code.` : 'We will email you a 6-digit code.'
      }
      heading="Locked out? You will be back in a minute."
      points={[
        'A 6-digit code, sent to your email',
        'Choose a new password, then sign in with it',
        'Your applications and profile stay exactly as they were',
      ]}
      footer={BACK_TO_SIGN_IN}
    >
      {sentTo ? (
        <ResetCodeStep
          email={sentTo}
          debugCode={debugCode}
          onResend={() => void resend()}
          isResending={resending}
          onAccepted={(code) =>
            // The code travels in navigation state, never in the address bar.
            navigate(paths.resetPassword, { state: { email: sentTo, code } })
          }
        />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {errors.root && <Alert tone="error">{errors.root.message}</Alert>}
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            required
            error={errors.email?.message}
            {...register('email')}
          />
          <Button type="submit" fullWidth isLoading={isSubmitting}>
            Send reset code
          </Button>
        </form>
      )}
    </AuthCard>
  );
}

/**
 * The new-password page. Reached from the code step with `{ email, code }` in
 * navigation state; opened any other way (a bookmark, a link with `?email=`) it
 * asks for the code first.
 */
export function ResetPasswordPage() {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [checked, setChecked] = useState(
    state?.email && state?.code ? { email: state.email, code: state.code } : null,
  );
  const [email, setEmail] = useState(state?.email ?? searchParams.get('email') ?? '');
  const [askingForCode, setAskingForCode] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(newPasswordSchema) });

  async function onSubmit(values) {
    try {
      await authApi.resetPassword({
        email: checked.email,
        code: checked.code,
        new_password: values.new_password,
      });
      toast.success('Password updated. Sign in with your new password.');
      navigate(paths.login, { replace: true });
    } catch (error) {
      setError('root', { message: errorMessage(error, 'Could not reset the password') });
    }
  }

  if (!checked) {
    return (
      <AuthCard
        title="Enter your code"
        description="Use the 6-digit code we emailed you."
        heading="Almost there."
        points={[
          'Use the 6-digit code from your email',
          'Then choose a new password',
          'Other devices are signed out when it changes',
        ]}
        footer={BACK_TO_SIGN_IN}
      >
        {askingForCode ? (
          <ResetCodeStep email={email} onAccepted={(code) => setChecked({ email, code })} />
        ) : (
          <form
            className="space-y-4"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              if (email.trim()) setAskingForCode(true);
            }}
          >
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value.trim())}
            />
            <Button type="submit" fullWidth disabled={!email.trim()}>
              Continue
            </Button>
            <p className="text-ink-muted text-center text-sm">
              No code yet?{' '}
              <Link to={paths.forgotPassword} className="text-brand-600 font-medium">
                Request one
              </Link>
            </p>
          </form>
        )}
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Choose a new password"
      description={`For ${checked.email}.`}
      heading="Almost there."
      points={[
        'At least 8 characters, with upper and lower case and a number',
        'Tap the eye to check what you typed',
        'Other devices are signed out when it changes',
      ]}
      footer={BACK_TO_SIGN_IN}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {errors.root && (
          <Alert tone="error">
            {errors.root.message}{' '}
            <Link to={paths.forgotPassword} className="font-medium underline">
              Request a new code
            </Link>
          </Alert>
        )}
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          autoFocus
          required
          error={errors.new_password?.message}
          {...register('new_password')}
        />
        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          required
          error={errors.confirm_password?.message}
          {...register('confirm_password')}
        />
        <Button type="submit" fullWidth isLoading={isSubmitting}>
          Update password
        </Button>
      </form>
    </AuthCard>
  );
}
